import { createContext, useState, useEffect } from "react";
import PropTypes from "prop-types";

const safeJsonParse = (value, fallback) => {
  // localStorage can contain legacy values like "undefined" or non-JSON strings.
  if (value == null || value === "" || value === "undefined") return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};
// Create the context
const DataContext = createContext();

// Create the provider component
const DataProvider = ({ children }) => {

  const [gateway, setGateway] = useState(
    localStorage.getItem("gateway") || ""
  );

  const [active, setActive] = useState("Orders"); // Default active icon

  const [orgName, setOrgName] = useState(
    localStorage.getItem("organisationName") || ""
  );
  const [metaData, setMetaData] = useState(() => {
    const storedMetaData = localStorage.getItem("metaData");
    return safeJsonParse(storedMetaData, {});
  });


  const [externalTokensList, setExternalTokensList] = useState({})

  const [invoiceNumber, setInvoiceNumber] = useState(0);

  const [userHasSeenTour, setUserHasSeenTour] = useState(false);

  const [mailId, setMailId] = useState(
    localStorage.getItem("loginEmail") || ""
  );
  const [userRole, setUserRole] = useState(() => {
    const storedUserRole = localStorage.getItem("loginRole");
    return safeJsonParse(storedUserRole, "USER");
  });
  const [isBetaUser, setIsBetaUser] = useState(
    localStorage.getItem("type") || ""
  );

  const [profileStatus, setProfileStatus] = useState(() => {
    const storedProfileStatus = localStorage.getItem("profileStatus");
    return safeJsonParse(storedProfileStatus, false);
  });

  const [profileComplete, setProfileComplete] = useState({});

  const [orderId, setOrderId] = useState(
    sessionStorage.getItem("selectedOrderId") || ""
  );
  const [orderNumber, setOrderNumber] = useState(
    sessionStorage.getItem("selectedOrderNo") || ""
  );
  const [isTimezoneAvailabe, setIsTimezoneAvailabe] = useState(
    localStorage.getItem("timeZone") || false
  );
  const [userId, setUserId] = useState(
    localStorage.getItem("loginUserId") || ""
  );
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [FLName, setFLName] = useState(localStorage.getItem("fullName") || "");
  const [firstName, setFirstName] = useState(localStorage.getItem("firstName") || "");
  const [lastName, setLastName] = useState(localStorage.getItem("lastName") || "");
  const [orderStage, setOrderStage] = useState(
    localStorage.getItem("selectedOrderStage") || ""
  );

  const [orderHistory, setOrderHistory] = useState(
    localStorage.getItem("selectedOrderHistory") || {
      "Order Started": null,
      "Payment Pending": null,
      "Order Placed": null,
      LICENSING: null,
      "Setup In Progress": null,
      "REQUIRED_INFO": null,
      "QUALITY_CHECK": null,
      DELIVERED: null,
    }
  );

  // Customer useStates
  const [customerId, setCustomerId] = useState(
    localStorage.getItem("selectedCustomerId") || ""
  );
  const [loginCustomerId, setLoginCustomerId] = useState(
    localStorage.getItem("loginCustomerId") || ""
  );

  const [selectedCustomerDetails, setSelectedCustomerDetails] = useState(
    localStorage.getItem("selectedCustomerFullDetails") || ""
  );
  const [tempCustomerId, setTempCustomerId] = useState(
    localStorage.getItem("tempId") || ""
  );

  const [joinedDate, setJoinedDate] = useState(
    localStorage.getItem("jId") || ""
  );

  //Ticket Number
  const [ticketNumber, setTicketNumber] = useState(
    localStorage.getItem("tkId") || ""
  );


  const [statusCode, setStatusCode] = useState(localStorage.getItem("statusCode") || "");

  // Store full order data from API response to avoid duplicate API calls
  const [orderData, setOrderData] = useState(null);

  // Store connected app data from API response to avoid duplicate API calls
  const [connectedAppData, setConnectedAppData] = useState(null);

  // Store user data from API response to avoid duplicate API calls
  const [userData, setUserData] = useState(null);

  // Load data from localStorage on component mount
  useEffect(() => {
    const storedOrgName = localStorage.getItem("organisationName");
    const storedMetaData = localStorage.getItem("metaData");
    const storedUserRole = localStorage.getItem("loginRole");
    const storedProfileStatus = localStorage.getItem("profileStatus");
    const storedMailId = localStorage.getItem("loginEmail");
    const storedOrderId = sessionStorage.getItem("selectedOrderId");
    const storedOrderNumber = sessionStorage.getItem("selectedOrderNo");
    const storedUserId = localStorage.getItem("loginUserId");
    const storedToken = localStorage.getItem("token");
    const storedFLName = localStorage.getItem("fullName");
    const storedIsBetaUser = localStorage.getItem("type");
    const storedTimeZone = localStorage.getItem("timeZone");
    const storedOrderStage = localStorage.getItem("selectedOrderStage");
    const storedOrderHistory = localStorage.getItem("selectedOrderHistory");
    const storedCustomerId = localStorage.getItem("selectedCustomerId");
    const storedLoginCustomerId = localStorage.getItem("loginCustomerId");
    const storedSelectedCustomerDetails = localStorage.getItem("selectedCustomerFullDetails");
    const storedSelectedTicketNumber = localStorage.getItem("tkId");
    const storedTempCustomerId = localStorage.getItem("tempId");
    const storedJoinedDate = localStorage.getItem("jId");
    const storedGateway = localStorage.getItem("gateway");

    if (storedGateway) setGateway(storedGateway);
    if (storedOrgName) setOrgName(storedOrgName);
    // Already initialized in useState, but re-syncing just in case (though mostly redundant now)
    // if (storedMetaData) setMetaData(safeJsonParse(storedMetaData, {}));
    // if (storedUserRole) setUserRole(safeJsonParse(storedUserRole, "USER"));
    // if (storedProfileStatus) setProfileStatus(safeJsonParse(storedProfileStatus, false));
    if (storedMailId) setMailId(storedMailId);
    if (storedOrderId) setOrderId(storedOrderId);
    if (storedOrderNumber) setOrderNumber(storedOrderNumber);
    if (storedUserId) setUserId(storedUserId);
    if (storedToken) setToken(storedToken);
    if (storedFLName) setFLName(storedFLName);
    if (storedIsBetaUser) setIsBetaUser(storedIsBetaUser);
    if (storedTimeZone) setIsTimezoneAvailabe(storedTimeZone);
    if (storedOrderStage) setOrderStage(storedOrderStage);
    if (storedOrderHistory) setOrderHistory(storedOrderHistory);
    if (storedCustomerId) setCustomerId(storedCustomerId);
    if (storedLoginCustomerId) setLoginCustomerId(storedLoginCustomerId);
    if (storedSelectedCustomerDetails) setSelectedCustomerDetails(storedSelectedCustomerDetails);
    if (storedSelectedTicketNumber) setTicketNumber(storedSelectedTicketNumber);
    if (storedTempCustomerId) setTempCustomerId(storedTempCustomerId);
    if (storedJoinedDate) setJoinedDate(storedJoinedDate);
  }, []);

  // Save data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("gateway", gateway);
  }, [gateway]);

  useEffect(() => {
    localStorage.setItem("organisationName", orgName);
  }, [orgName]);

  useEffect(() => {
    localStorage.setItem("metaData", JSON.stringify(metaData));
  }, [metaData]);

  useEffect(() => {
    localStorage.setItem("loginRole", JSON.stringify(userRole));
  }, [userRole]);

  useEffect(() => {
    localStorage.setItem("profileStatus", JSON.stringify(profileStatus));
  }, [profileStatus]);

  useEffect(() => {
    localStorage.setItem("loginEmail", mailId);
  }, [mailId]);

  useEffect(() => {
    localStorage.setItem("type", isBetaUser);
  }, [isBetaUser]);

  useEffect(() => {
    sessionStorage.setItem("selectedOrderId", orderId);
  }, [orderId]);

  useEffect(() => {
    sessionStorage.setItem("selectedOrderNo", orderNumber);
  }, [orderNumber]);

  useEffect(() => {
    localStorage.setItem("loginUserId", userId);
  }, [userId]);

  useEffect(() => {
    localStorage.setItem("token", token);
  }, [token]);

  useEffect(() => {
    localStorage.setItem("fullName", FLName);
  }, [FLName]);

  useEffect(() => {
    localStorage.setItem("firstName", firstName);
  }, [firstName]);

  useEffect(() => {
    localStorage.setItem("lastName", lastName);
  }, [lastName]);

  useEffect(() => {
    localStorage.setItem("timeZone", isTimezoneAvailabe);
  }, [isTimezoneAvailabe]);

  useEffect(() => {
    localStorage.setItem("selectedOrderStage", orderStage);
  }, [orderStage]);

  useEffect(() => {
    localStorage.setItem("selectedOrderHistory", orderHistory);
  }, [orderHistory]);

  useEffect(() => {
    localStorage.setItem("selectedCustomerId", customerId);
  }, [customerId]);

  useEffect(() => {
    localStorage.setItem("loginCustomerId", loginCustomerId);
  }, [loginCustomerId]);

  useEffect(() => {
    localStorage.setItem("selectedCustomerFullDetails", selectedCustomerDetails);
  }, [selectedCustomerDetails]);

  useEffect(() => {
    localStorage.setItem("tkId", ticketNumber);
  }, [ticketNumber]);

  useEffect(() => {
    localStorage.setItem("tempId", tempCustomerId);
  }, [tempCustomerId]);

  useEffect(() => {
    localStorage.setItem("jId", joinedDate);
  }, [joinedDate]);

  // Clear the LocalStorage
  const clearStorage = () => {
    localStorage.clear();
    sessionStorage.clear();
  };

  return (
    <DataContext.Provider
      value={{
        orgName,
        setOrgName,
        metaData,
        setMetaData,
        mailId,
        setMailId,
        orderId,
        setOrderId,
        userId,
        setUserId,
        token,
        setToken,
        FLName,
        setFLName,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        orderNumber,
        setOrderNumber,
        clearStorage,
        isTimezoneAvailabe,
        setIsTimezoneAvailabe,
        orderStage,
        setOrderStage,
        profileStatus,
        setProfileStatus,
        userRole,
        setUserRole,
        customerId,
        setCustomerId,
        orderHistory,
        setOrderHistory,
        loginCustomerId,
        setLoginCustomerId,
        selectedCustomerDetails,
        setSelectedCustomerDetails,
        ticketNumber,
        setTicketNumber,
        tempCustomerId,
        setTempCustomerId,
        isBetaUser,
        setIsBetaUser,
        statusCode,
        setStatusCode,
        setProfileComplete,
        profileComplete,
        setUserHasSeenTour,
        userHasSeenTour,
        setInvoiceNumber,
        invoiceNumber,
        setExternalTokensList,
        externalTokensList,
        gateway,
        setGateway,
        setActive,
        active,
        orderData,
        setOrderData,
        connectedAppData,
        setConnectedAppData,
        userData,
        setUserData,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

// PropTypes validation for DataProvider component
DataProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { DataContext, DataProvider };
